import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { storeConstraintContext, clearConstraintContext } from '../../lib/constraintContext';

/**
 * ConstraintContextBanner — shown on modelling destinations
 * (ROE Modeller, Solution Finder) when the user arrived via the
 * Impact Modelling "Current Opportunity" banner. Reads the
 * constraint context from the URL so the user lands mid-flow with
 * the Mission Control framing intact. Self-hides without params.
 */
export default function ConstraintContextBanner({ hint }) {
  const [searchParams] = useSearchParams();
  const [dismissed, setDismissed] = useState(false);

  const label = searchParams.get('constraint_label');
  const leak = parseInt(searchParams.get('leak'), 10);
  const recovery = parseInt(searchParams.get('recovery'), 10);
  const attenuated = searchParams.get('attenuated') === '1';

  // Persist the constraint for the rest of the session so scenario
  // saves made from this modelling flow get tagged with it.
  useEffect(() => {
    if (label && Number.isFinite(leak)) {
      storeConstraintContext({
        stage: searchParams.get('constraint_stage'),
        label,
        leak,
        recovery: Number.isFinite(recovery) ? recovery : null,
        attenuated,
      });
    }
  }, [label, leak, recovery, attenuated, searchParams]);

  if (dismissed || !label || !Number.isFinite(leak)) return null;

  return (
    <div
      className="rounded-xl border border-orange-200 bg-gradient-to-r from-amber-50 to-orange-50/70 px-4 py-3 flex items-center gap-3"
      data-testid="constraint-context-banner"
    >
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 ring-1 ring-orange-200">
        <AlertTriangle className="h-4 w-4 text-orange-600" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-orange-700">From Mission Control · Current Highest Opportunity</p>
        <p className="text-sm font-semibold text-slate-900 leading-snug" data-testid="constraint-context-headline">
          {label} is reducing value transfer by {leak}%.
          {Number.isFinite(recovery) && (
            <span className="text-emerald-700"> Target recovery: {attenuated ? `+${recovery}%` : `up to +${recovery}%`}.</span>
          )}
        </p>
        {hint && <p className="text-xs text-slate-600 mt-0.5">{hint}</p>}
      </div>
      <button
        type="button"
        onClick={() => { clearConstraintContext(); setDismissed(true); }}
        className="text-slate-400 hover:text-slate-600 flex-shrink-0"
        aria-label="Dismiss constraint context"
        data-testid="constraint-context-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
